import type { ButtonHTMLAttributes } from 'react';
import * as Tooltip from '@radix-ui/react-tooltip';
import { css, cva, type RecipeVariantProps } from '../../styled-system/css';
import type { SystemStyleObject } from '../../styled-system/types';
import { styled } from '../../styled-system/jsx';

export const buttonRecipe = cva({
    base: {
        position: 'relative',
        color: 'white',
        rounded: 'md!',
        width: 'fit-content',
        transition: '0.3s cubic-bezier(0.4, 0, 0.2, 1)',

        _active: {
            filter: 'brightness(0.92) saturate(1.1)',
        },

        _disabled: {
            opacity: 0.3,
            filter: 'grayscale(1)',
            pointerEvents: 'none',
        },
    },
    variants: {
        visual: {
            solid: {
                bg: 'main.solid.1',
                color: 'white',

                _hover: {
                    bg: 'main.solid.2',
                },
            },
            outline: {
                borderWidth: '1px',
                borderColor: 'main.interactive.3',
                color: 'main.text.1',
                _hover: {
                    bg: 'main.background.2',
                },
            },
            ghost: {
                color: 'main.text.1',
                bg: 'white/50',

                _hover: {
                    bg: 'white/90',
                },
            },
            icon: {
                borderWidth: '1px',
                borderColor: 'main.interactive.3',
                color: 'main.text.1',
                bg: 'main.background.1',
                rounded: 'full!', // !important for bootstrap
                px: '0',
                py: '0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '40px',

                _hover: {
                    bg: 'main.background.2',
                },
            },
        },
        size: {
            sm: { px: '6', py: '2', fontSize: '1rem' },
            lg: { px: '8', py: '4', fontSize: '1.4rem' },
            iconXs: {
                px: '0',
                py: '0',
                width: '26px',
                height: '26px',
                fontSize: '13px',
            },
            iconSm: {
                px: '0',
                py: '0',
                width: '40px',
                height: '40px',
                fontSize: '20px',
            },
            iconMd: {
                px: '0',
                py: '0',
                width: '60px',
                height: '60px',
                fontSize: '30px',
            },
        },
        color: {
            primary: {},
            danger: {
                bg: 'white',
                color: 'red.700',

                _hover: {
                    bg: 'red.50'
                }
            },
        },
    },
    compoundVariants: [{
        color: 'primary',
        visual: 'ghost',
        css: {
            bg: 'white/50',

            _hover: {
                bg: 'white/90',
            },
        },
    },
    ],
    defaultVariants: {
        visual: 'solid',
        color: 'primary',
        size: 'sm',
    },
});

type ButtonVariants = RecipeVariantProps<typeof buttonRecipe>;

export const Button = ({
    children,
    visual,
    size,
    tooltip,
    color,
    css: cssProp,
    ...props
}: ButtonHTMLAttributes<HTMLButtonElement> &
    ButtonVariants & { css?: SystemStyleObject; tooltip?: string }) => {
    const buttonProps = buttonRecipe.raw({ visual, size, color });

    if (tooltip) {
        return (
            <Tooltip.Provider>
            <Tooltip.Root delayDuration= { 200} >
            <Tooltip.Trigger asChild >
            <button
              { ...props }
        className = { css(buttonProps, cssProp) }
            >
            { children }
            </button>
            </Tooltip.Trigger>
            < Tooltip.Portal >
            <TooltipContent sideOffset={ 5 }>
                { tooltip }
                < TooltipArrow />
                </TooltipContent>
                </Tooltip.Portal>
                </Tooltip.Root>
                </Tooltip.Provider>
    );
  }

return (
    <button
      { ...props }
      className = { css(buttonProps, cssProp) }
    >
    { children }
    </button>
  );
};

const TooltipContent = styled(Tooltip.Content, {
    base: {
        borderRadius: 'md',
        zIndex: 9,
        padding: '10px 15px',
        lineHeight: 1,
        color: 'main.text.2',
        backgroundColor: 'white',
        boxShadow:
            'hsl(206 22% 7% / 35%) 0px 10px 38px -10px, hsl(206 22% 7% / 20%) 0px 10px 20px -15px',
        userSelect: 'none',
        animationDuration: '200ms',
        animationTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
        willChange: 'transform, opacity',
        '&[data-state="delayed-open"]': {
            '&[data-side="top"]': { animationName: 'slideDownAndFade' },
            '&[data-side="right"]': { animationName: 'slideLeftAndFade' },
            '&[data-side="bottom"]': { animationName: 'slideUpAndFade' },
            '&[data-side="left"]': { animationName: 'slideRightAndFade' },
        },
    },
});

const TooltipArrow = styled(Tooltip.Arrow, {
    base: {
        fill: 'white',
    },
});
